import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, limit, startAfter } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { SettingsNav } from './Branches';
import { IconDownload, IconFilter, IconShieldCheck, IconFileTypePdf } from '@tabler/icons-react';
import { AUDIT_LABELS, getAuditColour } from '../../utils/audit';
import { generateAuditLogPDF } from '../../utils/pdf';
import Papa from 'papaparse';

const PAGE_SIZE = 50;

export default function AuditLog() {
  const { orgId, userProfile, org } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [filterAction, setFilterAction] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [exportingPdf, setExportingPdf] = useState(false);

  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    const loadBranches = async () => {
      if (!resolvedOrgId) return;
      try {
        const snap = await getDocs(collection(db, 'organisations', resolvedOrgId, 'branches'));
        setBranches(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
    };
    loadBranches();
  }, [resolvedOrgId]);

  useEffect(() => {
    loadEntries(true);
  }, [resolvedOrgId, filterAction, filterBranch]);

  const loadEntries = async (reset = false) => {
    if (!resolvedOrgId) return;
    setLoading(true);
    try {
      let q = query(
        collection(db, 'organisations', resolvedOrgId, 'auditLog'),
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE)
      );

      if (!reset && lastDoc) {
        q = query(
          collection(db, 'organisations', resolvedOrgId, 'auditLog'),
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(PAGE_SIZE)
        );
      }

      const snap = await getDocs(q);
      let docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      if (filterAction) docs = docs.filter(d => d.action === filterAction);
      if (filterBranch) docs = docs.filter(d => d.branchId === filterBranch);

      setEntries(reset ? docs : prev => [...prev, ...docs]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getInitials = name => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const avatarColours = [
    { bg: '#E6F1FB', color: '#185FA5' },
    { bg: '#EAF3DE', color: '#3B6D11' },
    { bg: '#EEEDFE', color: '#534AB7' },
    { bg: '#FCEBEB', color: '#A32D2D' },
    { bg: '#FDF0E8', color: '#993C1D' },
    { bg: '#E1F5EE', color: '#085041' },
  ];

  const handleExportCSV = () => {
    const data = entries.map(e => ({
      'Date & time': formatTime(e.createdAt),
      'Action': AUDIT_LABELS[e.action] || e.action,
      'Performed by': e.performedByName || '—',
      'Target': e.targetName || e.targetId || '—',
      'Target type': e.targetType || '—',
      'Branch': e.branchId || '—',
      'Details': e.details || '—',
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    setExportingPdf(true);
    try {
      const dateRange = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      generateAuditLogPDF(entries, org, dateRange);
    } catch (err) {
      console.error(err);
    }
    setExportingPdf(false);
  };

  const ACTION_GROUPS = [
    { label: 'Assets', actions: ['asset.created', 'asset.updated', 'asset.deleted', 'asset.blocked', 'asset.unblocked'] },
    { label: 'Checks', actions: ['check.submitted', 'check.failed', 'check.passed'] },
    { label: 'Incidents', actions: ['incident.created', 'incident.updated', 'incident.closed'] },
    { label: 'Users', actions: ['user.invited', 'user.activated', 'user.role_changed', 'user.removed', 'user.profile_updated'] },
    { label: 'Fuel', actions: ['derv.order_placed', 'derv.level_updated', 'derv.tank_created', 'derv.tank_updated', 'fuel.fill_added'] },
    { label: 'Templates', actions: ['template.created', 'template.updated', 'template.deleted'] },
    { label: 'Organisation', actions: ['branch.created', 'branch.updated', 'org.settings_updated', 'org.plan_changed'] },
    { label: 'Auth', actions: ['auth.login', 'auth.logout'] },
  ];

  return (
    <Layout>
      <div style={{ display: 'flex', flex: 1, minHeight: '100vh' }}>
        <SettingsNav active="Audit log" />
        <div style={{ flex: 1, padding: 'var(--main-padding)' }}>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Audit log</h1>
              <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                Full activity history · {entries.length} entries loaded
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleExportCSV}
                disabled={entries.length === 0}
                style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '7px 14px', fontSize: 12, cursor: entries.length === 0 ? 'not-allowed' : 'pointer', opacity: entries.length === 0 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <IconDownload size={14} stroke={1.5} /> Export CSV
              </button>
              <button
                onClick={handleExportPDF}
                disabled={entries.length === 0 || exportingPdf}
                style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '7px 14px', fontSize: 12, cursor: entries.length === 0 || exportingPdf ? 'not-allowed' : 'pointer', opacity: entries.length === 0 || exportingPdf ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <IconFileTypePdf size={14} stroke={1.5} /> {exportingPdf ? 'Generating...' : 'Export PDF'}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              <IconFilter size={14} stroke={1.5} /> Filter:
            </div>
            <select
              value={filterAction}
              onChange={e => setFilterAction(e.target.value)}
              style={{ fontSize: 12, padding: '5px 10px', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
            >
              <option value="">All actions</option>
              {ACTION_GROUPS.map(group => (
                <optgroup key={group.label} label={group.label}>
                  {group.actions.map(action => (
                    <option key={action} value={action}>{AUDIT_LABELS[action] || action}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <select
              value={filterBranch}
              onChange={e => setFilterBranch(e.target.value)}
              style={{ fontSize: 12, padding: '5px 10px', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
            >
              <option value="">All branches</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            {(filterAction || filterBranch) && (
              <button
                onClick={() => { setFilterAction(''); setFilterBranch(''); }}
                style={{ fontSize: 12, color: 'var(--color-brand)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Table */}
          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.6fr 1.4fr 1fr 0.8fr', gap: 0, padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
              {['Date & time', 'Action', 'Performed by', 'Target', 'Branch'].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 400, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
              ))}
            </div>

            {loading ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading audit log...</div>
            ) : entries.length === 0 ? (
              <div style={{ padding: '48px 32px', textAlign: 'center' }}>
                <IconShieldCheck size={32} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 12 }} />
                <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 6 }}>No audit entries yet</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Actions taken across the platform will appear here</div>
              </div>
            ) : entries.map((entry, i) => {
              const colours = avatarColours[i % avatarColours.length];
              const actionColour = getAuditColour(entry.action);
              return (
                <div
                  key={entry.id}
                  style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.6fr 1.4fr 1fr 0.8fr', gap: 0, padding: '11px 16px', borderBottom: i < entries.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatTime(entry.createdAt)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: actionColour.bg, color: actionColour.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {AUDIT_LABELS[entry.action] || entry.action}
                    </span>
                    {entry.details && (
                      <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {entry.details}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: colours.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: colours.color, flexShrink: 0 }}>
                      {getInitials(entry.performedByName)}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.performedByName || '—'}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.targetName || entry.targetId || '—'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                    {branches.find(b => b.id === entry.branchId)?.name || entry.branchId || '—'}
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                onClick={() => loadEntries(false)}
                style={{ background: 'transparent', color: 'var(--color-brand)', border: '0.5px solid var(--color-brand)', borderRadius: 'var(--radius-md)', padding: '8px 24px', fontSize: 13, cursor: 'pointer' }}
              >
                Load more
              </button>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
}